import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, formatGel, formatGelShort, formatPoints } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useDashboard } from '../lib/queries';
import type { DashboardData } from '../lib/types';
import { useSession } from '../lib/session';
import { Amount, Card, EmptyState, SectionTitle, Skeleton, Stat, TierBadge, Pill } from '../components/ui';

/** What the shop owes its people, today and this month, after refunds. */
function StaffPayCard({ pay }: { pay: NonNullable<DashboardData['staffPay']> }) {
  const { t } = useI18n();
  return (
    <Card>
      <SectionTitle>{t('pay.dashboardTitle')}</SectionTitle>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Amount value={formatGel(pay.todayTetri)} className="block text-xl font-bold text-ink" />
          <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('pay.today')}</div>
        </div>
        <div>
          <Amount value={formatGel(pay.monthTetri)} className="block text-xl font-bold text-ink-dim" />
          <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">{t('pay.month')}</div>
        </div>
      </div>
      {pay.people.length === 0 ? (
        <p className="mt-3 text-xs text-ink-faint">{t('pay.nobodyYet')}</p>
      ) : (
        <ul className="mt-3 space-y-1.5 border-t border-line pt-3">
          {pay.people.map((person) => (
            <li key={person.adminUserId} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-ink-dim">{person.name}</span>
              <span className="tabular shrink-0 text-ink">
                {formatGel(person.todayTetri)}
                <span className="ml-2 text-[0.7rem] text-ink-faint">{formatGel(person.monthTetri)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const CHART_DAYS = 14;

/** The last fortnight as calendar days, today last, with nothing where nothing was sold. */
function fortnight(days: Array<{ day: string; revenueTetri: number; orders: number }>) {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const today = new Date();
  return Array.from({ length: CHART_DAYS }, (_, index) => {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (CHART_DAYS - 1 - index));
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return { date, revenueTetri: byDay.get(key)?.revenueTetri ?? 0, orders: byDay.get(key)?.orders ?? 0 };
  });
}

function RevenueChart({ days }: { days: Array<{ day: string; revenueTetri: number; orders: number }> }) {
  const { t } = useI18n();
  // Hover has no meaning on a phone; there a tap picks the day to read.
  const [picked, setPicked] = useState<number | null>(null);
  if (days.length === 0) return null;
  const series = fortnight(days);
  const peak = Math.max(...series.map((d) => d.revenueTetri), 1);

  return (
    <Card>
      <SectionTitle>{t('dash.last14')}</SectionTitle>
      {/*
        Each column is as tall as the chart, so a bar's percentage height has
        something to be a percentage of — without it every bar came out at zero.
      */}
      <div className="flex h-28 items-stretch gap-1" role="img" aria-label={t('dash.revenueByDay')}>
        {series.map((day, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setPicked(picked === index ? null : index)}
            className="group relative flex h-full flex-1 flex-col justify-end"
            aria-label={`${formatDate(day.date.toISOString())}: ${formatGelShort(day.revenueTetri)}`}
          >
            <span
              className={`block w-full rounded-t transition-colors ${
                day.revenueTetri > 0 ? 'bg-brand/60 group-hover:bg-brand' : 'bg-line'
              } ${picked === index ? 'bg-brand' : ''}`}
              style={{ height: day.revenueTetri > 0 ? `${Math.max(4, (day.revenueTetri / peak) * 100)}%` : '2px' }}
            />
            <span className={`pointer-events-none absolute -top-7 z-10 whitespace-nowrap rounded-md border border-line bg-raised px-2 py-1 text-[0.62rem] text-ink group-hover:block ${
              picked === index ? 'block' : 'hidden'
            } ${index < 3 ? 'left-0' : index > CHART_DAYS - 4 ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}>
              {formatGelShort(day.revenueTetri)} · {day.orders}
            </span>
          </button>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[0.62rem] text-ink-faint">
        <span>{formatDate(series[0]!.date.toISOString())}</span>
        <span>{formatDate(series.at(-1)!.date.toISOString())}</span>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const { t } = useI18n();
  const { data: session } = useSession();
  const { data, isPending } = useDashboard();

  if (isPending) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        <Skeleton className="h-16" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { today, customers, liability, tierDistribution } = data;

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{t('dash.today')}</p>
        <h1 className="mt-2 font-display text-[1.75rem] font-bold tracking-tight text-ink sm:text-[2rem]">
          {t('dash.greeting', { name: session?.firstName })}
        </h1>
      </header>

      <section className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t('dash.revenueToday')}
          value={<Amount value={formatGel(today.revenueTetri)} />}
          sub={t('dash.ordersCount', { count: today.orders })}
        />
        <Stat
          label={t('dash.cashbackToday')}
          value={<Amount value={formatGel(today.cashbackEarnedTetri)} className="text-positive" />}
          sub={today.cashbackUsedTetri > 0
            ? t('dash.redeemed', { amount: formatGel(today.cashbackUsedTetri) })
            : t('dash.noneRedeemed')}
        />
        <Stat
          label={t('dash.customers')}
          value={customers.total}
          sub={customers.newToday > 0
            ? t('dash.newToday', { count: customers.newToday })
            : t('dash.noneNewToday')}
        />
        <Stat
          label={t('dash.loyaltyOwed')}
          value={<Amount value={formatGel(liability.cashbackOutstandingTetri)} className="text-brand" />}
          sub={liability.cashbackPendingTetri > 0
            ? t('dash.plusMaturing', { amount: formatGel(liability.cashbackPendingTetri) })
            : t('dash.pointsOutstanding', { points: formatPoints(liability.pointsOutstanding) })}
        />
      </section>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="min-w-0 space-y-4">
          <RevenueChart days={data.revenueByDay} />

          <section>
            <SectionTitle>{t('dash.recentOrders')}</SectionTitle>
            {data.recentOrders.length === 0
              ? <EmptyState title={t('dash.noOrdersYet')} />
              : (
                <div className="space-y-2">
                  {data.recentOrders.map((order) => (
                    <Link
                      key={order.id}
                      to={`/orders/${order.id}`}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-3 transition-colors hover:border-brand/40 hover:bg-raised"
                    >
                      <div className="min-w-0">
                        <div className="tabular text-sm font-semibold text-ink">{order.reference}</div>
                        <div className="mt-0.5 truncate text-xs text-ink-faint">
                          {order.customerName}
                          <span className="mx-1.5 opacity-50">·</span>
                          {formatDate(order.completedAt)}
                        </div>
                      </div>
                      <Amount
                        value={formatGel(order.orderValueTetri)}
                        className="shrink-0 text-sm font-semibold text-ink"
                      />
                    </Link>
                  ))}
                </div>
              )}
          </section>
        </div>

        <aside className="min-w-0 space-y-4">
          {data.staffPay && <StaffPayCard pay={data.staffPay} />}
          <Card>
            <SectionTitle>{t('dash.tierDistribution')}</SectionTitle>
            <div className="space-y-2">
              {tierDistribution.map((tier) => {
                const total = tierDistribution.reduce((t, x) => t + x.customers, 0) || 1;
                return (
                  <div key={tier.code}>
                    <div className="flex items-center justify-between gap-2">
                      <TierBadge code={tier.code} size="sm" />
                      <span className="tabular text-sm text-ink">{tier.customers}</span>
                    </div>
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-brand/60"
                        style={{ width: `${(tier.customers / total) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <SectionTitle>{t('dash.goneQuiet')}</SectionTitle>
            {data.lapsedHighValue.length === 0 ? (
              <p className="text-sm text-ink-dim">
                {t('dash.nobodyQuiet', {
                  amount: formatGelShort(data.thresholds.highValueTetri),
                  days: data.thresholds.warningDays,
                })}
              </p>
            ) : (
              <div className="space-y-2">
                {data.lapsedHighValue.map((customer) => (
                  <Link
                    key={customer.id}
                    to={`/customers/${customer.id}`}
                    className="block rounded-xl border border-line bg-raised/40 px-3 py-2.5 transition-colors hover:border-brand/40"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-ink">{customer.name}</span>
                      <Pill tone={customer.stage === 'LOST' ? 'danger' : customer.stage === 'LAPSED' ? 'warning' : 'neutral'}>
                        {t(`dash.quiet.${customer.stage}`)}
                      </Pill>
                      {customer.tierCode && <TierBadge code={customer.tierCode} size="sm" />}
                    </div>
                    <div className="tabular mt-0.5 text-[0.68rem] text-ink-faint">
                      {formatGel(customer.lifetimeSpendTetri)}
                      <span className="mx-1.5 opacity-50">·</span>
                      {customer.lastVisitAt ? formatDate(customer.lastVisitAt) : t('customer.neverVisited')}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
